import api from '@/services/api';
import { notify } from '@/utils/globalFunctions';

/**
 * Composable para manejar shapes de BackgroundCanvas
 * Proporciona funciones reutilizables para parsear, serializar y guardar shapes
 */
export function useCanvasShapes() {
    /**
     * Parsea un JSON string de shapes a un array
     * @param {string} shapesJson - JSON string con los shapes
     * @returns {Array} - Array de shapes parseados
     */
    function parsedShapes(shapesJson) {
        if (!shapesJson || shapesJson === '[]' || shapesJson === '') {
            return [];
        }
        try {
            const parsed = JSON.parse(shapesJson);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('Error al parsear shapes:', error);
            return [];
        }
    }

    /**
     * Serializa shapes a JSON string para enviar a la API
     * @param {string|Array|Object} payload - Shapes a serializar
     * @returns {string} - JSON string
     */
    function serializeShapesForApi(payload) {
        // Si ya es string, retornarlo directamente
        if (typeof payload === 'string') return payload;

        try {
            return JSON.stringify(payload ?? []);
        } catch (error) {
            console.error('Error al serializar shapes:', error);
            return '[]';
        }
    }

    /**
     * Crea un handler para guardar shapes desde BackgroundCanvas
     * @param {Object} options - Opciones de configuración
     * @param {Object} options.formData - Objeto ref con los datos del formulario
     * @param {string} options.patientId - ID del paciente
     * @param {string} options.endpoint - Endpoint de la API (ej: '/pacientes/{id}/dentistry')
     * @param {string} options.logIcon - Icono para el console.log (opcional)
     * @param {string} options.successMessage - Mensaje de éxito personalizado (opcional)
     * @returns {Function} - Función handleSaveShapes
     */
    function createShapesHandler({
        formData,
        patientId,
        endpoint,
        logIcon = '💾',
        successMessage = 'Dibujo guardado'
    }) {
        return async function handleSaveShapes(newShapes, field) {
            const serialized = serializeShapesForApi(newShapes);

            // Actualizar formData
            formData.value[field] = serialized;

            // Guardar en la API
            try {
                const url = endpoint.replace('{id}', patientId);
                await api.post(url, {
                    [field]: serialized
                });

                console.log(`${logIcon} JSON guardado en el campo ${field}:`, serialized);

                // Mostrar notificación de éxito
                notify(
                    'Guardado exitoso',
                    successMessage,
                    'success',
                    'fa-regular fa-circle-check'
                );
            } catch (error) {
                console.error(`❌ Error al guardar ${field}:`, error);

                // Mostrar notificación de error
                notify(
                    'Error al guardar',
                    'No se pudo guardar el dibujo. Por favor, intenta de nuevo.',
                    'danger',
                    'fa-regular fa-circle-xmark'
                );

                throw error;
            }
        };
    }

    return {
        parsedShapes,
        serializeShapesForApi,
        createShapesHandler
    };
}
